import { useState, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { DeviceItem } from "@rago/web-shared";
import { api } from "../../../lib/apiClient";
import { auth } from "../../../lib/authManager";
import { validatePassword } from "../../../lib/authForm";
import {
  deviceKind,
  familyCounts,
  platformFamily,
  productLabel,
  sessionCountLabel,
  sortSessions,
  type DeviceKind,
  type FamilyCount,
} from "../../../lib/sessions";
import { fullDateTime, lastActive } from "../../../lib/timeAgo";
import { useAuth } from "../../../lib/useAuth";
import { LaptopIcon, MonitorIcon, PhoneIcon, TabletIcon, TvIcon } from "../../../components/icons";
import { chipClass, chipCountClass } from "../../../components/library/chip";
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  MenuTriggerChevron,
} from "../../../components/menu";
import { PasswordInput } from "../../../components/password-input";
import {
  SheetFooter,
  SheetHeader,
  SheetInset,
  SheetMessage,
  SheetRow,
  SheetSection,
} from "../../../components/settings-sheet";
import { Chip, ErrorNote, Field, PasswordChecklist, Spinner, StatusDot } from "../../../components/ui";

export const Route = createFileRoute("/_authed/settings/security")({
  component: Security,
});

function Security() {
  const state = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = state.status === "signedIn" ? state.user : null;
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changed, setChanged] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [resendError, setResendError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setChanged(false);
    try {
      await api("/v1/auth/password/change", { body: { currentPassword, newPassword } });
      setChanged(true);
      setCurrentPassword("");
      setNewPassword("");
      // The server signed every other device out; the list below must say so.
      await qc.invalidateQueries({ queryKey: ["devices"] });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification() {
    setBusy(true);
    setResendError(null);
    try {
      await api("/v1/auth/resend-verification", { method: "POST", body: {} });
      setResent(true);
    } catch (err) {
      setResendError(err);
    } finally {
      setBusy(false);
    }
  }

  async function signOutEverywhere() {
    await auth.signOut(true);
    await navigate({ to: "/auth/signin", search: { next: undefined } });
  }

  return (
    <div>
      <SheetHeader title="Security" />

      {user && !user.emailVerified ? (
        <SheetSection title="Email">
          <ErrorNote error={resendError} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[var(--rago-warn)]">Your email isn't verified yet.</p>
            {resent ? (
              <p className="text-sm text-[var(--rago-ok)]">Verification mail sent again.</p>
            ) : (
              <button
                type="button"
                className="rago-btn rago-btn-quiet rago-btn-sm"
                onClick={() => void resendVerification()}
                disabled={busy}
              >
                Resend verification mail
              </button>
            )}
          </div>
        </SheetSection>
      ) : null}

      <form onSubmit={changePassword} noValidate>
        <SheetSection title="Change password">
          <Field label="Current password" htmlFor="current">
            <input
              id="current"
              type="password"
              autoComplete="current-password"
              className="rago-input"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Field>
          <Field label="New password" htmlFor="new">
            <PasswordInput
              id="new"
              autoComplete="new-password"
              value={newPassword}
              onChange={setNewPassword}
            >
              <PasswordChecklist password={newPassword} />
            </PasswordInput>
          </Field>
          <SheetFooter
            bare
            label="Change password"
            saving={busy}
            saved={changed}
            savedNote="Password changed. Other devices were signed out."
            error={error}
            disabled={!currentPassword || validatePassword(newPassword) !== undefined}
          />
        </SheetSection>
      </form>

      <SheetSection title="Sessions" flush>
        <SheetMessage>Revoking a session signs that device out immediately.</SheetMessage>
        <SessionList onSignOutEverywhere={() => void signOutEverywhere()} />
      </SheetSection>
    </div>
  );
}

/**
 * The table's md+ columns: device · app/version · last active · action. The
 * list owns the tracks and every row subgrids into them, so the action
 * column is sized once for all rows — a row without a Revoke button (this
 * device) and the header keep the same column edges as the rest.
 */
const COLS = "md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,0.8fr)_auto]";
const ROW = "md:col-span-full md:grid-cols-subgrid";

/**
 * The account's live sessions (GET /v1/devices, one row per device): a
 * platform filter, a count, the table and the page-wide "Sign out
 * everywhere" as its trailing row so the hairlines run through.
 */
function SessionList(props: { onSignOutEverywhere: () => void }) {
  const qc = useQueryClient();
  const [family, setFamily] = useState("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const devices = useQuery({
    queryKey: ["devices"],
    queryFn: () => api<{ items: DeviceItem[] }>("/v1/devices"),
  });

  async function revoke(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await api(`/v1/devices/${id}`, { method: "DELETE" });
      await qc.invalidateQueries({ queryKey: ["devices"] });
    } catch (err) {
      setError(err);
    } finally {
      setBusyId(null);
    }
  }

  const all = sortSessions(devices.data?.items ?? []);
  const { total, families } = familyCounts(all);
  // A pick whose last row was revoked falls back to everything — derived,
  // so the trigger and the rows agree on the very render the row leaves.
  const active = family !== "all" && !families.some((f) => f.name === family) ? "all" : family;
  const visible = active === "all" ? all : all.filter((d) => platformFamily(d) === active);
  const problem = error ?? devices.error;

  return (
    <>
      {problem ? (
        <SheetInset>
          <ErrorNote error={problem} />
        </SheetInset>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3 px-[var(--rago-sheet-x)] pt-3 pb-4">
        <PlatformFilter value={active} total={total} families={families} onChange={setFamily} />
        <p className="text-sm tabular-nums text-[var(--rago-text-3)]">
          {devices.isLoading ? "" : sessionCountLabel(visible.length)}
        </p>
      </div>
      <ul
        className={`divide-y divide-[var(--rago-line-1)] max-md:border-t max-md:border-[var(--rago-line-1)] md:grid md:gap-x-4 ${COLS}`}
      >
        <li aria-hidden className={`hidden md:grid ${ROW} px-[var(--rago-sheet-x)] pb-2.5`}>
          <span className="rago-caps">Device</span>
          <span className="rago-caps">App · Version</span>
          <span className="rago-caps">Last active</span>
          <span />
        </li>
        {devices.isLoading ? (
          <li className="py-3 md:col-span-full">
            <SheetMessage loading>Loading…</SheetMessage>
          </li>
        ) : visible.length === 0 ? (
          <li className="py-3 md:col-span-full">
            <SheetMessage>{active === "all" ? "No sessions." : `No ${active} sessions.`}</SheetMessage>
          </li>
        ) : (
          visible.map((d) => (
            <SessionRow key={d.id} item={d} busy={busyId === d.id} onRevoke={() => void revoke(d.id)} />
          ))
        )}
        <SheetRow className="pt-4 md:col-span-full">
          <p className="text-sm text-[var(--rago-text-3)]">
            Signs you out on every device, including this one.
          </p>
          <button
            type="button"
            className="shrink-0 rounded-sm text-sm font-medium text-[var(--rago-error)] hover:underline focus-visible:ring-2 focus-visible:ring-[var(--rago-ring)] focus-visible:outline-none"
            onClick={props.onSignOutEverywhere}
          >
            Sign out everywhere
          </button>
        </SheetRow>
      </ul>
    </>
  );
}

/** The platform pill: a radio menu whose rows carry their counts. */
function PlatformFilter(props: {
  value: string;
  total: number;
  families: FamilyCount[];
  onChange: (value: string) => void;
}) {
  const label = props.value === "all" ? "All platforms" : props.value;
  // Unchecked rows reserve the check's 16px + gap so every count shares one right edge.
  const count = (n: number, checked: boolean) => (
    <span className={`${chipCountClass} ${checked ? "" : "mr-6"}`}>{n}</span>
  );
  return (
    <Menu>
      <MenuTrigger className={chipClass} aria-label={`Platform: ${label}`}>
        {label}
        <MenuTriggerChevron size={14} className="text-[var(--rago-text-3)]" />
      </MenuTrigger>
      <MenuContent align="start" width="sm">
        <MenuRadioGroup value={props.value} onValueChange={props.onChange}>
          <MenuRadioItem value="all" trailing={count(props.total, props.value === "all")}>
            All platforms
          </MenuRadioItem>
          {props.families.map((f) => (
            <MenuRadioItem key={f.name} value={f.name} trailing={count(f.count, props.value === f.name)}>
              {f.name}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}

const KIND_ICON: Record<DeviceKind, typeof MonitorIcon> = {
  laptop: LaptopIcon,
  tv: TvIcon,
  tablet: TabletIcon,
  phone: PhoneIcon,
  monitor: MonitorIcon,
};

/**
 * One session. Four grid cells that read as a table row at md+ and, on a
 * phone, stack app/version and last-active under the name (52px = the
 * 40px tile + its gap) with the action pinned top-right.
 */
function SessionRow(props: { item: DeviceItem; busy: boolean; onRevoke: () => void }) {
  const d = props.item;
  const { label, live } = d.current
    ? { label: "Active now", live: true }
    : lastActive(d.session.lastUsedAt);
  return (
    <li
      className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 px-[var(--rago-sheet-x)] py-3.5 ${ROW} md:gap-y-0`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <DeviceTile kind={deviceKind(d.platform)} current={d.current} />
        <div className="min-w-0">
          {/* The chip may wrap under a long name on a phone; at md+ it stays on
              the name's line and the name truncates instead. */}
          <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-medium text-[var(--rago-text-1)] md:flex-nowrap">
            <span className="truncate">{d.name}</span>
            {d.current ? (
              <span className="shrink-0">
                <Chip tone="ok">This device</Chip>
              </span>
            ) : null}
          </p>
          <p className="mt-0.5 text-xs text-[var(--rago-text-3)]">{d.platform}</p>
        </div>
      </div>
      {/* Inline on a phone, the badge under the name at md+ (the mockup's stack). */}
      <div className="col-start-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 pl-[52px] text-sm text-[var(--rago-text-2)] md:col-auto md:flex-col md:items-start md:pl-0">
        <span className="sr-only">App: </span>
        <span className="max-w-full truncate">{productLabel(d.product)}</span>
        {d.version ? <VersionBadge>{d.version}</VersionBadge> : null}
      </div>
      <div
        className="col-start-1 flex items-center pl-[52px] text-sm text-[var(--rago-text-2)] md:col-auto md:pl-0"
        title={fullDateTime(d.session.lastUsedAt)}
      >
        <span className="sr-only">Last active: </span>
        <StatusDot online={live} />
        <span className={live ? "text-[var(--rago-text-1)]" : undefined}>{label}</span>
      </div>
      <div className="col-start-2 row-start-1 flex justify-end md:col-auto md:row-auto">
        {d.current ? null : (
          <button
            type="button"
            className="rago-btn rago-btn-ghost-danger rago-btn-sm"
            disabled={props.busy}
            onClick={props.onRevoke}
            aria-label={`Revoke ${d.name}`}
          >
            {props.busy ? <Spinner /> : "Revoke"}
          </button>
        )}
      </div>
    </li>
  );
}

/** The row's glyph tile; this device's is lit in the "ok" green, like its chip. */
function DeviceTile(props: { kind: DeviceKind; current: boolean }) {
  const Glyph = KIND_ICON[props.kind];
  return (
    <span
      aria-hidden
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--rago-radius-m)] border ${
        props.current
          ? "border-[rgba(62,207,142,0.35)] bg-[rgba(62,207,142,0.12)] text-[var(--rago-ok)]"
          : "border-[var(--rago-line-1)] bg-white/5 text-[var(--rago-text-2)]"
      }`}
    >
      <Glyph size={20} />
    </span>
  );
}

function VersionBadge(props: { children: ReactNode }) {
  return (
    <span className="inline-flex h-5 items-center rounded-[var(--rago-radius-s)] border border-[var(--rago-line-1)] bg-white/5 px-1.5 font-mono text-[11px] tabular-nums text-[var(--rago-text-3)]">
      {props.children}
    </span>
  );
}
